'use client';

import { useCallback, useMemo } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { ShortCategoryListItemViewModel } from '@/types/cml-api';
import { VideosFilterContext } from './context';
import { VideosFilterContextType, VideosFilterProviderProps } from './types';
import { ApiParamsKeys } from '../types';
import { useGetCategories } from '../utils/use-get-categories';
import { useGetLabels } from '../utils/use-get-labels';
export const VideosFilterProvider = ({
  children
}: VideosFilterProviderProps) => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const {
    data: labels,
    isLoading: isLabelsLoading
  } = useGetLabels();
  const {
    data: categories,
    isLoading: isCategoriesLoading
  } = useGetCategories();
  const updateSearchParams = useCallback((updatedParams: URLSearchParams) => {
    const newUrl = `${window.location.pathname}?${updatedParams.toString()}`;
    router.push(newUrl);
  }, [router]);
  const toggleMultiSelectFilter = useCallback((key: ApiParamsKeys, value: string | number) => {
    const params = new URLSearchParams(searchParams);
    const existingValues = params.getAll(key);
    const stringifiedValue = value.toString();
    if (existingValues.includes(stringifiedValue)) {
      params.delete(key);
      existingValues.filter(existing => existing !== stringifiedValue).forEach(filtered => params.append(key, filtered));
    } else {
      params.append(key, stringifiedValue);
    }
    updateSearchParams(params);
  }, [searchParams, updateSearchParams]);
  const toggleSingleSelectFilter = useCallback((key: ApiParamsKeys, value: string | number) => {
    const params = new URLSearchParams(searchParams);
    const currentValue = params.get(key);
    const stringifiedValue = value.toString();
    if (currentValue === stringifiedValue) {
      params.delete(key);
    } else {
      params.set(key, stringifiedValue);
    }
    updateSearchParams(params);
  }, [searchParams, updateSearchParams]);
  const clearFilter = useCallback((key: ApiParamsKeys) => {
    const params = new URLSearchParams(searchParams);
    params.delete(key);
    updateSearchParams(params);
  }, [searchParams, updateSearchParams]);
  const activeCategories = useMemo(() => {
    const ids = searchParams.getAll(ApiParamsKeys.Category);
    const list = ids.map(id => categories?.find(category => category.id?.value?.toString() === id) || null).filter(Boolean) as ShortCategoryListItemViewModel[];
    return list;
  }, [searchParams, categories]);
  const activeLabels = useMemo(() => searchParams.getAll(ApiParamsKeys.Label), [searchParams]);
  const clearAllFilters = useCallback(() => {
    const params = new URLSearchParams(searchParams);
    params.delete(ApiParamsKeys.Category);
    params.delete(ApiParamsKeys.Label);
    updateSearchParams(params);
  }, [searchParams, updateSearchParams]);
  const toggleCategory = useCallback((value: number) => toggleSingleSelectFilter(ApiParamsKeys.Category, value), [toggleSingleSelectFilter]);
  const toggleLabel = useCallback((value: string) => toggleMultiSelectFilter(ApiParamsKeys.Label, value), [toggleMultiSelectFilter]);
  const clearCategories = useCallback(() => clearFilter(ApiParamsKeys.Category), [clearFilter]);
  const clearLabels = useCallback(() => clearFilter(ApiParamsKeys.Label), [clearFilter]);
  const contextValue: VideosFilterContextType = useMemo(() => ({
    activeCategories,
    activeLabels,
    toggleCategory,
    toggleLabel,
    clearCategories,
    clearLabels,
    clearAllFilters,
    labels,
    categories,
    isLabelsLoading,
    isCategoriesLoading
  }), [activeCategories, activeLabels, toggleCategory, toggleLabel, clearCategories, clearLabels, clearAllFilters, labels, categories, isLabelsLoading, isCategoriesLoading]);
  return <VideosFilterContext.Provider value={contextValue} data-sentry-element="unknown" data-sentry-component="VideosFilterProvider" data-sentry-source-file="context-provider.tsx">{children}</VideosFilterContext.Provider>;
};